import { useState, useEffect, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Particles } from '../components/Particles';
import { authApi } from '../lib/api';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Verificar mensagens na URL
    const urlParams = new URLSearchParams(window.location.search);
    const urlMessage = urlParams.get('message');

    if (urlMessage === 'logged_out') {
      setMessage({ text: 'Você saiu da sua conta com sucesso.', type: 'success' });
    } else if (urlMessage === 'session_expired') {
      setMessage({ text: 'Sua sessão expirou. Por favor, faça login novamente.', type: 'info' });
    } else if (urlMessage === 'password_updated') {
      setMessage({ text: 'Senha alterada com sucesso! Faça login com sua nova senha.', type: 'success' });
    } else if (urlMessage === 'check_email') {
      setMessage({ text: 'Conta criada com sucesso! Verifique seu email para confirmar o cadastro e ativar sua conta.', type: 'success' });
    } else if (urlMessage === 'email_verified') {
      setMessage({ text: 'Email verificado com sucesso! Você já está logado.', type: 'success' });
    }

    // Carregar email salvo se existir
    const savedEmail = localStorage.getItem('remember_email');
    if (savedEmail) {
      setEmail(savedEmail);
      setRemember(true);
    }
  }, []);

  const showMessage = (text: string, type: 'success' | 'error' | 'info') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      showMessage('Por favor, preencha todos os campos.', 'error');
      return;
    }

    // Salvar ou remover email do localStorage
    if (remember) {
      localStorage.setItem('remember_email', email);
    } else {
      localStorage.removeItem('remember_email');
    }

    setIsLoading(true);

    try {
      const response = await authApi.login({ email, password });

      // Salvar tokens e dados do usuário no localStorage do auth
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      localStorage.setItem('user', JSON.stringify(response.user));

      showMessage('Login realizado com sucesso! Redirecionando...', 'success');

      // Redirecionar para o athlete app (todos os usuários)
      setTimeout(() => {
        const token = encodeURIComponent(response.access_token);
        const refreshToken = encodeURIComponent(response.refresh_token);
        const athleteUrl = import.meta.env.VITE_ATHLETE_APP_URL || 'http://localhost:3002';
        window.location.href = `${athleteUrl}?access_token=${token}&refresh_token=${refreshToken}`;
      }, 1000);
    } catch (error: any) {
      console.error('Erro no login:', error);

      let errorMessage = 'Erro ao fazer login. Tente novamente.';

      if (error?.response?.status === 401) {
        errorMessage = 'Email ou senha incorretos.';
      } else if (error?.response?.status === 403) {
        const detail = error.response?.data?.detail || '';
        if (detail === 'account_disabled') {
          errorMessage = 'Problemas ao logar em sua conta, fale com o suporte.';
        } else if (detail.includes('email') || detail.includes('confirmado')) {
          errorMessage = 'Por favor, confirme seu email antes de fazer login. Verifique sua caixa de entrada.';
        } else {
          errorMessage = detail || 'Acesso negado.';
        }
      } else if (error?.response?.status === 429) {
        errorMessage = 'Muitas tentativas. Aguarde alguns minutos.';
      } else if (!error?.response) {
        errorMessage = 'Não foi possível conectar ao servidor. Verifique sua conexão.';
      }

      showMessage(errorMessage, 'error');
      setIsLoading(false);
    }
  };

  return (
    <>
      <Particles />

      <div className="auth-container">
        {/* Logo */}
        <div className="auth-logo">
          <img src="/images/logo/logo.png" alt="NeuroAtleta Mindset" />
          <h1>Bem-vindo de volta</h1>
          <p>Entre com sua conta para continuar</p>
        </div>

        {/* Mensagens */}
        {message && (
          <div id="message-container">
            <div className={`message ${message.type}`}>{message.text}</div>
          </div>
        )}

        {/* Formulário de Login */}
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="seu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
            <span className="error-message">Email inválido</span>
          </div>

          <div className="form-group">
            <label htmlFor="password">Senha</label>
            <input
              type="password"
              id="password"
              name="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              autoComplete="current-password"
            />
            <span className="error-message">A senha deve ter no mínimo 6 caracteres</span>
          </div>

          <div className="checkbox-group">
            <input
              type="checkbox"
              id="remember"
              name="remember"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
            />
            <label htmlFor="remember">Lembrar-me</label>
          </div>

          <button type="submit" className="btn-primary" id="submitBtn" disabled={isLoading}>
            {isLoading ? (
              <>
                <span className="spinner"></span> Entrando...
              </>
            ) : (
              'Entrar'
            )}
          </button>
        </form>

        {/* Links */}
        <div className="auth-links">
          <Link to="/forgot-password" className="auth-link">
            Esqueceu sua senha?
          </Link>
          <div className="divider">
            <span>ou</span>
          </div>
          <Link to="/register" className="btn-secondary">
            Criar uma conta
          </Link>
        </div>

        {/* Voltar para home */}
        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <a href={`${import.meta.env.VITE_WEB_URL || 'http://localhost:3000'}/`} className="back-link">
            ← Voltar para o site
          </a>
        </div>
      </div>
    </>
  );
}
